import {
  IAccountRateCreateReqSchemaUI,
  IAccountRateUpdateReqSchemaUI,
  IAccountRespSchema,
  IPriceResSchema,
  IPriceUnderProductSchemaUI,
  IPricesAndOptionsReqSchema,
  IQuoteOfferingRespSchema,
  IRateReqSchemaUI,
  IRateResSchema,
  PriceModelEnum,
  RateTypeEnum,
} from '@monetize/types/app';
import { UseFormSetError } from 'react-hook-form';
import { QuoteOfferingRateEditReq } from '../../../api/quoteOfferingRateService';
import { logger } from '../../../services/logger';

export function applyRateEditToRate(
  rate: IRateResSchema,
  {
    name,
    internalDescription,
    prices,
    percentOfTotalConfig,
  }: QuoteOfferingRateEditReq,
): IRateResSchema {
  const savedPricesByProductId = (rate.prices ?? []).reduce<
    Record<string, IPriceResSchema[]>
  >((acc, price) => {
    acc[price.product.id] = [...(acc[price.product.id] ?? []), price];
    return acc;
  }, {});
  const tierCountByProductId: Record<string, number> = {};

  const updatedPrices = prices.reduce<IPriceResSchema[]>((acc, price) => {
    const productId = price.productId ?? '';
    const savedPrices = savedPricesByProductId[productId];
    if (!savedPrices?.length) {
      return acc;
    }

    const tierIndex = tierCountByProductId[productId] ?? 0;
    tierCountByProductId[productId] = tierIndex + 1;
    const savedPrice = savedPrices[tierIndex];

    acc.push({
      ...(savedPrice ?? savedPrices[0]),
      id: savedPrice ? savedPrice.id : `${productId}-new-${tierIndex}`,
      from: Number(price.from),
      to: price.to ?? null,
      amount: Number(price.amount),
      priceModel: price.priceModel,
      blockSize: price.blockSize ?? null,
      description: price.description ?? null,
    });
    return acc;
  }, []);

  return {
    ...rate,
    name,
    internalDescription: internalDescription ?? null,
    percentOfTotalConfig,
    prices: updatedPrices,
  };
}

export function prepareRateRequestData(
  account: IAccountRespSchema | Pick<IAccountRespSchema, 'id' | 'accountName'>,
  productsWithPrices: IPriceUnderProductSchemaUI[],
  offeringRate: IRateResSchema,
): IAccountRateCreateReqSchemaUI | IAccountRateUpdateReqSchemaUI {
  const rateProducts: Record<string, IPricesAndOptionsReqSchema> =
    productsWithPrices.reduce((acc, curr) => {
      const prices = curr.prices || [];
      const currentOption = offeringRate.options?.productOptions?.find(
        ({ productId }) => productId === curr.id,
      );

      return {
        ...acc,
        [curr.id]: {
          prices: prices.map((product: IPriceResSchema) => ({
            productId: curr.id,
            from: product.from,
            to:
              product.to === null || product.to === undefined
                ? null
                : product.to,
            amount: product.amount,
            priceModel: product.priceModel,
            description: null, // product.description ? String(product.description) : '',
            blockSize: product.blockSize, // for block pricing
          })),
          productType: curr.productType,
          aggregationModel: currentOption?.aggregationModel,
        },
      };
    }, {});

  return {
    name: (offeringRate.name || '').substring(0, 50),
    rateType: RateTypeEnum.ACCOUNT as RateTypeEnum.ACCOUNT,
    accountId: account.id,
    products: rateProducts,
    billingFrequency: offeringRate.billingFrequency,
    internalDescription: offeringRate.internalDescription,
    parentRateId: offeringRate.parentRateId || offeringRate.id,
    percentOfTotalConfig: offeringRate.percentOfTotalConfig,
    customFields: offeringRate.customFields,
  };
}

/**
 * Validates block pricing to ensure "to" field is a multiple of blockSize
 * @param formData - The form data containing products with prices
 * @param setError - React Hook Form setError function
 * @returns true if there are validation errors, false otherwise
 */
export function validateBlockPricing<
  T extends
    | IRateReqSchemaUI
    | IAccountRateCreateReqSchemaUI
    | IAccountRateUpdateReqSchemaUI,
>(formData: T, setError: UseFormSetError<T>): boolean {
  let hasError = false;

  for (const [productId, productData] of Object.entries(formData.products)) {
    if (productData.prices) {
      for (let index = 0; index < productData.prices.length; index++) {
        const price = productData.prices[index];
        if (
          price.priceModel === PriceModelEnum.BLOCK &&
          price.blockSize &&
          price.to !== null &&
          !isNaN(Number(price.to))
        ) {
          const toValue = Number(price.to);
          if (toValue % price.blockSize !== 0) {
            setError(`products.${productId}.prices.${index}.to` as any, {
              type: 'validate',
              message: `Value must be a multiple of block size (${price.blockSize})`,
            });
            hasError = true;
          }
        }
      }
    }
  }

  if (hasError) {
    logger.error(
      'Block pricing validation failed: "to" must be a multiple of block size',
    );
  }

  return hasError;
}

/**
 * Collapses each eligible product's price tiers to the single tier that
 * matches the current quote quantity. Non-eligible products are returned
 * unchanged.
 */
export function resolveProductTiers(
  productsWithPrices: IPriceUnderProductSchemaUI[],
  eligibleProductIds: ReadonlySet<string>,
  quoteOffering: IQuoteOfferingRespSchema,
): IPriceUnderProductSchemaUI[] {
  return productsWithPrices.map((product) => {
    if (!eligibleProductIds.has(product.id)) {
      return product;
    }

    const quoteItem = quoteOffering.items.find(
      (item) => item.productId === product.id,
    );
    const quantity = quoteItem?.quantity ?? 0;

    const matchingTier = product.prices.find(
      ({ from, to }) => from <= quantity && (to === null || quantity <= to),
    );

    if (!matchingTier) {
      return product;
    }

    // For CUSTOM price model, use the quote item's unitPrice as the locked amount
    const amount =
      matchingTier.priceModel === PriceModelEnum.CUSTOM
        ? (quoteItem?.unitPrice ?? matchingTier.amount)
        : matchingTier.amount;

    return {
      ...product,
      prices: [{ ...matchingTier, from: quantity, to: null, amount }],
    };
  });
}
